import type { WorkflowSharedTypes } from '@openphone/dexie-database/types'
import { makeAutoObservable, observable } from 'mobx'

import type Service from '.'
import PersistedCollection from './collections/PersistedCollection'
import type { RawWorkflowFilterDefinition } from './model/workflow/WorkflowFilterDefinitionModel'
import WorkflowFilterDefinitionModel from './model/workflow/WorkflowFilterDefinitionModel'
import { isBranchingEnabled } from './utils/isBranchingEnabled'
import type { WorkflowFilterDefinitionRepository } from './worker/repository/WorkflowFilterDefinitionRepository'
import { WORKFLOW_FILTER_DEFINITION_TABLE_NAME } from './worker/repository/WorkflowFilterDefinitionRepository'

export default class WorkflowFilterDefinitionStore {
  private readonly collection: PersistedCollection<
    WorkflowFilterDefinitionModel,
    WorkflowFilterDefinitionRepository
  >
  private fetched = false

  constructor(private root: Service) {
    this.collection = new PersistedCollection({
      table: root.storage.table(WORKFLOW_FILTER_DEFINITION_TABLE_NAME),
      classConstructor: (json: RawWorkflowFilterDefinition) =>
        new WorkflowFilterDefinitionModel(json),
    })

    makeAutoObservable<this, 'collection'>(this, {
      collection: observable,
    })

    if (isBranchingEnabled(this.root.flags.flags)) {
      void this.load()
    }
  }

  get supportedFilterDefinitionIds(): ReadonlySet<WorkflowSharedTypes.FilterDefinitionId> {
    return new Set(
      this.collection.list
        .filter((definition) => definition.supportLevel !== 'unsupported')
        .map((definition) => definition.id),
    )
  }

  getFilterDefinitionById(
    filterDefinitionId: WorkflowSharedTypes.FilterDefinitionId,
  ): WorkflowFilterDefinitionModel | null {
    return this.collection.get(filterDefinitionId) ?? null
  }

  fetchFilterDefinitions() {
    if (this.fetched || !isBranchingEnabled(this.root.flags.flags)) {
      return
    }

    this.fetched = true

    void this.root.transport.workflow.filterDefinitions
      .list(1000)
      .then((response) => {
        const models = response.data.map(
          (item) => new WorkflowFilterDefinitionModel(item),
        )
        this.collection.putBulk(models)
      })
      .catch(() => {
        this.fetched = false
      })
  }

  private load() {
    return this.collection.performQuery((repo) => repo.all())
  }
}
